/**
 * Entrada escalonada de listas — fuente ÚNICA para todo el producto.
 *
 * Reglas que codifica (y que los tests de `__tests__/list-entrance.test.ts`
 * protegen, porque se rompen solas):
 *
 * 1. **De una sola vez por navegación, NUNCA por refresco de datos.** La app es
 *    local-first sobre PowerSync: una sola mutación dispara una RÁFAGA de
 *    invalidaciones (≈6 refetches). Si el efecto observara el array de datos,
 *    cada refetch reiniciaría el temporizador, mantendría `isEntering` en true y
 *    volvería a reproducir la entrada escalonada → parpadeo visible. Por eso
 *    aquí solo se observa un BOOLEANO derivado ("¿hay filas?") más el `scope`.
 *
 * 2. **Escalonado con tope.** Una lista de 200 filas no puede encolar 200
 *    animaciones: el retardo se satura en {@link MAX_STAGGER_DELAY_MS}, así que
 *    a partir de la fila ~7 todas entran a la vez. El coste de compositor es
 *    constante, no proporcional al tamaño de la lista.
 *
 * 3. **Solo `opacity` + `transform`** (ver `@keyframes list-entrance` en
 *    `styles/global.css`). Sin `width`/`height`/`top`/`box-shadow`: nada que
 *    obligue a recalcular layout ni a repintar.
 *
 * 4. **Sin `will-change`.** La animación dura 200 ms y arranca en el mismo
 *    frame del montaje; reservar capa de compositor de forma permanente en
 *    cientos de filas cuesta más memoria en móvil de lo que ahorra.
 *
 * 5. **Nunca deja opacidad 0 persistente.** `animation-fill-mode: both` fija el
 *    estado final (opacidad 1) y, en cuanto la entrada termina, el hook devuelve
 *    `false` y las clases/estilos desaparecen del DOM. Playwright y los lectores
 *    de pantalla ven el contenido presente y pulsable desde el primer frame.
 */
import { type Accessor, createEffect, createMemo, createSignal, onCleanup } from "solid-js";

/** Retardo entre filas consecutivas. */
export const STAGGER_DELAY_MS = 24;
/** Tope del retardo acumulado: a partir de aquí todas las filas entran juntas. */
export const MAX_STAGGER_DELAY_MS = 150;
/** Debe coincidir con la duración de `@keyframes list-entrance` en global.css. */
export const ENTRANCE_DURATION_MS = 200;

/**
 * Ámbitos que YA hicieron su entrada en esta sesión de navegación. Vive a nivel
 * de módulo (no por componente) para que volver a una lista ya vista —
 * remontando el componente— no vuelva a animarla.
 */
const enteredScopes = new Set<string>();

/** Solo para tests: vacía el registro de ámbitos ya animados. */
export function resetListEntrance(): void {
  enteredScopes.clear();
}

/**
 * Retardo del escalonado para la fila `index`, saturado en
 * {@link MAX_STAGGER_DELAY_MS}.
 */
export function entranceDelayMs(index: number): number {
  return Math.min(Math.max(index, 0) * STAGGER_DELAY_MS, MAX_STAGGER_DELAY_MS);
}

/**
 * Props a esparcir sobre cada fila/tarjeta de la lista.
 *
 * Cuando `isEntering` es `false` devuelve `undefined` en ambos campos: no queda
 * ni clase de animación ni `style` residual en el DOM.
 */
export function listEntranceProps(
  isEntering: boolean,
  index: number,
): { class: string | undefined; style: Record<string, string> | undefined } {
  if (!isEntering) {
    return { class: undefined, style: undefined };
  }
  return {
    class: "animate-list-entrance",
    style: { "animation-delay": `${entranceDelayMs(index)}ms` },
  };
}

interface UseListEntranceOptions {
  /**
   * Identidad de la lista que se está mostrando (id de ruta, id de cohorte,
   * pestaña activa…). Cambiarlo REARMA la entrada; es lo que distingue "he
   * navegado a otra lista" de "han llegado datos nuevos de la misma lista".
   */
  scope: Accessor<string>;
  /**
   * `true` cuando la lista tiene al menos un elemento. Debe ser un BOOLEANO,
   * no el array: ver la regla 1 de la cabecera de este módulo.
   */
  hasItems: Accessor<boolean>;
}

/**
 * Devuelve `true` mientras la lista está haciendo su entrada escalonada.
 *
 * @example
 * const isEntering = useListEntrance({
 *   scope: () => `cohorts:${orgId()}`,
 *   hasItems: () => (cohorts.data?.length ?? 0) > 0,
 * });
 * <For each={cohorts.data}>
 *   {(row, i) => <li {...listEntranceProps(isEntering(), i())}>…</li>}
 * </For>
 */
export function useListEntrance(options: UseListEntranceOptions): Accessor<boolean> {
  const [enteredKey, setEnteredKey] = createSignal<string | null>(null);

  // Memo sobre el BOOLEANO: las ráfagas de refetch de PowerSync que devuelven
  // "sigue habiendo filas" no propagan cambio y, por tanto, no reinician el
  // temporizador de abajo. Este memo es el corazón de la regla 1.
  const hasItems = createMemo(() => options.hasItems());

  createEffect(() => {
    const key = options.scope();
    if (enteredScopes.has(key)) {
      setEnteredKey(key);
      return;
    }
    if (!hasItems()) {
      return;
    }
    const timer = setTimeout(() => {
      enteredScopes.add(key);
      setEnteredKey(key);
    }, ENTRANCE_DURATION_MS + MAX_STAGGER_DELAY_MS);
    onCleanup(() => {
      clearTimeout(timer);
    });
  });

  const isEntering = createMemo(() => hasItems() && enteredKey() !== options.scope());
  return isEntering;
}
